import { z } from 'zod';
/**
 * Payload shape for `GET /me/charging/revenue-series`.
 *
 * Driver-scoped time-series companion to the admin's
 * /api/dashboard/operator-summary `revenueLast24h` field. Same bucket
 * geometry (one entry per day of the chosen window, oldest-first),
 * scoped to one user's sessions instead of a tenant's chargers.
 *
 * No 24h period — drivers don't operate chargers, so hourly
 * granularity isn't useful.
 *
 * "Revenue" here is the driver's perspective on session cost — money
 * spent on charging, not money received as an operator. The chart
 * label on the PWA reads as a personal spend trend.
 */
// Widened for the History-page all-period chart: the route buckets any
// window by span (daily up to ~5 weeks, weekly up to ~6 months, else
// monthly). 'custom' echoes an explicit from/to request.
export const MyChargingRevenueSeriesPeriodSchema = z.enum(['7d', '30d', '90d', '1y', 'all', 'custom']);
export const MyChargingRevenueSeriesSchema = z.object({
    /** Bucketed spend in dollars, oldest first. Bucket granularity is
     *  span-derived server-side (daily / weekly / monthly), so length
     *  varies by window: 7 for 7d, 30 for 30d, 13 weekly for 90d, up
     *  to ~12 monthly for 1y. Empty windows return arrays of zeros
     *  rather than null — chart consumer doesn't special-case
     *  "no data". */
    buckets: z.array(z.number().nonnegative()),
    /** Window the buckets cover. Echoes back the request's `period` so
     *  the client can verify the response matches what it asked for. */
    window: MyChargingRevenueSeriesPeriodSchema,
    /** Sum of `buckets`, surfaced as a top-line stat for the chart's
     *  footer slot. Dollars (not cents), matching the bucket values. */
    totalSpent: z.number().nonnegative(),
    /** Number of qualifying sessions in the window. Drives the footer's
     *  "$XX.XX · N sessions" copy on the PWA. */
    sessionCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=me-charging-revenue-series.js.map